import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Leaf, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated. Welcome back!");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't reset password.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <main className="min-h-[calc(100vh-4rem)] bg-leaf-pattern flex items-center justify-center px-4 py-12" data-testid="reset-no-token">
        <Card className="w-full max-w-md rounded-2xl border-emerald-100">
          <CardContent className="p-8 text-center">
            <h1 className="font-display text-2xl text-emerald-950">Invalid link</h1>
            <p className="text-sm text-emerald-900/70 mt-3">
              This password reset link is missing a token. Please request a fresh one.
            </p>
            <Link to="/forgot-password">
              <Button className="mt-6 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="reset-request-new">
                Request a new link
              </Button>
            </Link>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-leaf-pattern flex items-center justify-center px-4 py-12" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-emerald-700 text-white mb-3">
            <Leaf className="w-6 h-6" />
          </span>
          <h1 className="font-display text-3xl text-emerald-950 font-light tracking-tight">
            Choose a new password
          </h1>
          <p className="text-emerald-900/60 mt-2 text-sm">
            Pick something strong and memorable — at least 8 characters.
          </p>
        </div>
        <Card className="rounded-2xl border-emerald-100">
          <CardContent className="p-6 sm:p-8">
            {done ? (
              <div className="text-center space-y-4" data-testid="reset-success">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-50 text-emerald-700">
                  <CheckCircle2 className="w-6 h-6" />
                </span>
                <h2 className="font-display text-xl text-emerald-950">Password updated</h2>
                <p className="text-sm text-emerald-900/70">
                  Redirecting you to login…
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="np" className="text-emerald-900">New password</Label>
                  <div className="relative">
                    <Input
                      id="np"
                      type={show ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="mt-1 rounded-xl border-emerald-200 focus-visible:ring-emerald-500/30 pr-10"
                      placeholder="At least 8 characters"
                      data-testid="reset-new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShow((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-700 hover:text-emerald-800"
                      aria-label={show ? "Hide password" : "Show password"}
                      data-testid="reset-toggle-show"
                    >
                      {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="cp" className="text-emerald-900">Confirm new password</Label>
                  <Input
                    id="cp"
                    type={show ? "text" : "password"}
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="mt-1 rounded-xl border-emerald-200 focus-visible:ring-emerald-500/30"
                    placeholder="Type it again"
                    data-testid="reset-confirm-password"
                  />
                </div>
                {error ? (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl p-3" data-testid="reset-error">
                    {error}
                  </div>
                ) : null}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-emerald-700 hover:bg-emerald-800 text-white py-6"
                  data-testid="reset-submit-btn"
                >
                  {loading ? "Updating…" : "Update password"}
                </Button>
                <p className="text-center text-sm text-emerald-900/70">
                  <Link to="/login" className="text-emerald-700 font-semibold hover:underline" data-testid="reset-to-login">
                    Back to login
                  </Link>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
